import { Button, Card } from '@kalos/ui';
import { AlertCircle, CheckCircle, CreditCard, Loader2 } from 'lucide-react';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';

import {
  closeHelcimPayIframe,
  ensureHelcimPayIframeStacking,
  waitForHelcimPayEmbedPainted,
} from './helcimPayTeardown';

declare global {
  interface Window {
    appendHelcimPayIframe?: (checkoutToken: string) => void;
    removeHelcimPayIframe?: () => void;
  }
}

interface LineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
  discount?: number;
}

export type HelcimCheckoutPresentation = 'full' | 'iframeOnly';

export type HelcimCheckoutMode = 'payment' | 'tokenize';

export type HelcimCheckoutVariant = 'default' | 'paymentLink';

interface HelcimCheckoutProps {
  logoUrl?: string;
  checkoutToken: string;
  amount: number;
  description: string;
  metadata?: {
    invoiceId?: number;
    jobNumber?: string;
    customerId?: number;
  };
  lineItems?: LineItem[];
  subtotal?: number;
  discountAmount?: number;
  taxAmount?: number;
  retainageAmount?: number;
  customerName?: string;
  propertyAddress?: string;
  onSuccess: (transactionId: string) => void;
  onCancel: () => void;
  onError?: (error: string) => void;
  onIframeClosedWithoutResponse?: () => void;
  /** Called right after a successful `appendHelcimPayIframe` (manual Pay or auto-open). */
  onPayIframeOpened?: () => void;
  showCancelButton?: boolean; // Optional: Show/hide cancel button (default true)
  /** `iframeOnly`: hide Kalos invoice summary and “Ready to Pay”; Helcim iframe only. */
  presentation?: HelcimCheckoutPresentation;
  /** When true with a ready token and script, open the Helcim Pay iframe once (no second in-app Pay click). */
  autoOpenPayIframe?: boolean;
  /** When true, do not render the internal success card after SUCCESS (parent shows toast and unmounts). */
  suppressInternalSuccessUI?: boolean;
  /** `tokenize`: verify-only session (zero amount); adjusts copy and hides purchase amount UI. */
  mode?: HelcimCheckoutMode;
  /** `paymentLink`: customer payment-link layout — dedicated Invoice Details card, no duplicate summary block, dark CTA. */
  variant?: HelcimCheckoutVariant;
  /** Invoice number for the payment-link Invoice Details card (falls back to metadata.invoiceId). */
  invoiceNumber?: string;
}

const HELCIM_JS_URL = 'https://secure.helcim.app/helcim-pay/services/start.js';

/** Keep Kalos “connecting” UI visible at least this long after append so captcha/checkout can paint. */
const MIN_HELCIM_CONNECT_OVERLAY_MS = 900;

async function notifyAfterHelcimEmbedReady(
  onPayIframeOpened: (() => void) | undefined,
  connectStartedMs: number,
  isCancelled: () => boolean,
): Promise<void> {
  if (!onPayIframeOpened) return;
  await waitForHelcimPayEmbedPainted();
  if (isCancelled()) return;
  const elapsed = performance.now() - connectStartedMs;
  const remain = MIN_HELCIM_CONNECT_OVERLAY_MS - elapsed;
  if (remain > 0) {
    await new Promise((r) => setTimeout(r, remain));
  }
  if (!isCancelled()) {
    onPayIframeOpened();
  }
}

export const HelcimCheckout: FC<HelcimCheckoutProps> = ({
  logoUrl,
  checkoutToken,
  amount,
  metadata,
  lineItems,
  subtotal,
  discountAmount,
  taxAmount,
  retainageAmount,
  customerName,
  propertyAddress,
  onSuccess,
  onCancel,
  onError,
  onIframeClosedWithoutResponse,
  onPayIframeOpened,
  showCancelButton = true, // Default to true for backward compatibility
  presentation = 'full',
  autoOpenPayIframe = false,
  suppressInternalSuccessUI = false,
  mode = 'payment',
  variant = 'default',
  invoiceNumber,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<
    'idle' | 'processing' | 'succeeded' | 'failed'
  >('idle');
  /** After SUCCESS when suppressInternalSuccessUI — parent unmounts; avoid flashing internal success UI. */
  const [externallyCompleted, setExternallyCompleted] = useState(false);
  const scriptLoadedRef = useRef(false);
  const iframeOpenRef = useRef(false);
  /** Set only after `appendHelcimPayIframe` succeeds so we can retry when the SDK is late to register. */
  const lastAutoOpenedTokenRef = useRef<string | null>(null);
  /**
   * Helcim’s `start.js` can finish loading before `window.appendHelcimPayIframe` exists.
   * iframeOnly + autoOpen waits on this instead of failing immediately.
   */
  const [openingHelcimSdk, setOpeningHelcimSdk] = useState(false);

  // Load Helcim Pay script
  useEffect(() => {
    if (scriptLoadedRef.current) {
      setIsLoading(false);
      return;
    }

    const existingScript = document.querySelector(`script[src="${HELCIM_JS_URL}"]`);
    if (existingScript) {
      scriptLoadedRef.current = true;
      // Tag is present; global may still be wiring — iframeOnly+autoOpen path polls for append().
      setIsLoading(false);
      return;
    }

    const script = document.createElement('script');
    script.src = HELCIM_JS_URL;
    script.async = true;
    script.onload = () => {
      scriptLoadedRef.current = true;
      setIsLoading(false);
    };
    script.onerror = () => {
      setError('Failed to load payment processor. Please refresh the page and try again.');
      setIsLoading(false);
    };
    document.head.appendChild(script);

    return () => {
      if (iframeOpenRef.current) {
        closeHelcimPayIframe();
        iframeOpenRef.current = false;
      }
    };
  }, []);

  // Listen for Helcim payment events (per Helcim docs: eventName is helcim-pay-js-{checkoutToken}, eventStatus is SUCCESS/ABORTED/HIDE)
  useEffect(() => {
    const helcimPayJsKey = `helcim-pay-js-${checkoutToken}`;

    const handleMessage = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== 'object') return;

      const { eventName, eventStatus, eventMessage } = event.data;
      if (eventName !== helcimPayJsKey) return;

      if (eventStatus === 'SUCCESS') {
        let transactionId = 'unknown';
        try {
          const msg = typeof eventMessage === 'string' ? JSON.parse(eventMessage) : eventMessage;
          transactionId =
            String(msg?.data?.data?.transactionId ?? msg?.transactionId ?? '') || 'unknown';
        } catch {
          transactionId = event.data.transactionId ?? 'unknown';
        }
        iframeOpenRef.current = false;
        closeHelcimPayIframe();
        if (suppressInternalSuccessUI) {
          setExternallyCompleted(true);
          onSuccess(transactionId);
        } else {
          setPaymentStatus('succeeded');
          onSuccess(transactionId);
        }
      } else if (eventStatus === 'ABORTED') {
        const errorMsg =
          (typeof eventMessage === 'string' ? eventMessage : eventMessage?.message) ||
          'Payment was declined. Please try again.';
        iframeOpenRef.current = false;
        closeHelcimPayIframe();
        setError(errorMsg);
        setPaymentStatus('idle');
        onError?.(errorMsg);
      } else if (eventStatus === 'HIDE') {
        iframeOpenRef.current = false;
        closeHelcimPayIframe();
        setPaymentStatus('idle');
        onCancel();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [checkoutToken, onSuccess, onCancel, onError, suppressInternalSuccessUI]);

  // Monitor iframe closure - if closed without a clear event, notify the parent
  // so it can refetch payment data and determine if the payment went through
  useEffect(() => {
    if (!iframeOpenRef.current || paymentStatus !== 'processing') return;

    const checkIframeClosure = setInterval(() => {
      const helcimIframe = document.querySelector('iframe[src*="helcim"]');

      if (!helcimIframe && iframeOpenRef.current) {
        iframeOpenRef.current = false;
        closeHelcimPayIframe();
        setPaymentStatus('idle');
        onIframeClosedWithoutResponse?.();
      }
    }, 500);

    return () => clearInterval(checkIframeClosure);
  }, [paymentStatus, onIframeClosedWithoutResponse]);

  const handlePayClick = useCallback(() => {
    if (!checkoutToken) {
      setError('Payment session not initialized. Please refresh and try again.');
      return;
    }

    if (!window.appendHelcimPayIframe) {
      setError('Payment processor not ready. Please refresh the page.');
      return;
    }

    setPaymentStatus('processing');
    setError(null);

    try {
      const connectStarted = performance.now();
      window.appendHelcimPayIframe(checkoutToken);
      iframeOpenRef.current = true;
      ensureHelcimPayIframeStacking();
      if (onPayIframeOpened) {
        void notifyAfterHelcimEmbedReady(onPayIframeOpened, connectStarted, () => false);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to open payment form';
      setError(errorMessage);
      setPaymentStatus('failed');
      onError?.(errorMessage);
    }
  }, [checkoutToken, onError, onPayIframeOpened]);

  const HELCIM_APPEND_POLL_MS = 50;
  const HELCIM_APPEND_WAIT_MS = 20_000;

  useEffect(() => {
    if (presentation !== 'iframeOnly' || !autoOpenPayIframe) return;
    if (!checkoutToken || isLoading) return;
    if (lastAutoOpenedTokenRef.current === checkoutToken) return;

    let cancelled = false;

    const run = async () => {
      setOpeningHelcimSdk(true);
      setError(null);
      const deadline = performance.now() + HELCIM_APPEND_WAIT_MS;

      try {
        while (!cancelled && performance.now() < deadline) {
          if (typeof window.appendHelcimPayIframe === 'function') {
            const connectStarted = performance.now();
            window.appendHelcimPayIframe(checkoutToken);
            iframeOpenRef.current = true;
            ensureHelcimPayIframeStacking();
            lastAutoOpenedTokenRef.current = checkoutToken;
            setPaymentStatus('processing');
            await notifyAfterHelcimEmbedReady(onPayIframeOpened, connectStarted, () => cancelled);
            return;
          }
          await new Promise((r) => setTimeout(r, HELCIM_APPEND_POLL_MS));
        }
        if (!cancelled) {
          const msg =
            'Payment processor is taking too long to start. Close and try again, or refresh the page.';
          setError(msg);
          setPaymentStatus('failed');
          onError?.(msg);
        }
      } catch (err) {
        if (!cancelled) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to open payment form';
          setError(errorMessage);
          setPaymentStatus('failed');
          onError?.(errorMessage);
        }
      } finally {
        setOpeningHelcimSdk(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [presentation, autoOpenPayIframe, checkoutToken, isLoading, onError, onPayIframeOpened]);

  const handleHelcimCancelButton = useCallback(() => {
    if (iframeOpenRef.current) {
      closeHelcimPayIframe();
      iframeOpenRef.current = false;
      setPaymentStatus('idle');
    }
    onCancel();
  }, [onCancel]);

  if (externallyCompleted) {
    return null;
  }

  if (paymentStatus === 'succeeded') {
    return (
      <Card className="border-green-200 bg-green-50 p-8 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="mb-2 text-xl font-semibold text-green-800">
          {mode === 'tokenize' ? 'Card saved' : 'Payment Successful!'}
        </h3>
        <p className="text-green-700">
          {mode === 'tokenize'
            ? 'Your card was verified and saved on file.'
            : `Your payment of $${amount.toFixed(2)} has been processed successfully.`}
        </p>
      </Card>
    );
  }

  if (presentation === 'iframeOnly') {
    if (error) {
      return (
        <Card className="border-red-200 bg-red-50 p-4">
          <div className="flex items-start">
            <AlertCircle className="mr-3 h-5 w-5 flex-shrink-0 text-red-600" />
            <div className="min-w-0 flex-1">
              <h4 className="font-medium text-red-800">Payment Error</h4>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <Button
                type="button"
                variant="outline"
                className="mt-4"
                onClick={() => {
                  setError(null);
                  setPaymentStatus('idle');
                  onCancel();
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </Card>
      );
    }
    if (isLoading || (openingHelcimSdk && !onPayIframeOpened)) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-12 text-gray-600">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" aria-hidden />
          <p className="text-sm">
            {isLoading
              ? mode === 'tokenize'
                ? 'Loading card verification…'
                : 'Loading payment processor…'
              : mode === 'tokenize'
                ? 'Opening secure card form…'
                : 'Opening secure payment…'}
          </p>
        </div>
      );
    }
    if (openingHelcimSdk && onPayIframeOpened) {
      return null;
    }
    return null;
  }

  return (
    <div className="w-full space-y-6">
      {/* Logo and Header */}
      <div className="text-center">
        {logoUrl && <img src={logoUrl} alt="Kalos Services" className="mx-auto mb-6 h-12 w-auto" />}
        <h1 className="mb-2 text-2xl font-bold text-gray-900">Secure Payment</h1>
        <p className="text-gray-600">Complete your payment for Kalos Services.</p>
      </div>

      {/* Invoice Details (payment-link layout) */}
      {variant === 'paymentLink' &&
        mode !== 'tokenize' &&
        (customerName || propertyAddress || invoiceNumber || metadata?.invoiceId) && (
          <Card className="bg-white p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Invoice Details</h2>
            <div className="space-y-2 text-sm">
              {customerName && (
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">Customer:</span>
                  <span className="text-right font-medium text-gray-900">{customerName}</span>
                </div>
              )}
              {propertyAddress && (
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">Property:</span>
                  <span className="text-right font-medium text-gray-900">{propertyAddress}</span>
                </div>
              )}
              {(invoiceNumber || metadata?.invoiceId) && (
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">Invoice #:</span>
                  <span className="text-right font-medium text-gray-900">
                    {invoiceNumber ?? metadata?.invoiceId}
                  </span>
                </div>
              )}
            </div>
          </Card>
        )}

      {/* Invoice Info, Amount Due, and Line Items - Single Card */}
      {mode !== 'tokenize' && lineItems && lineItems.length > 0 && (
        <Card className={`overflow-hidden p-0 ${variant === 'paymentLink' ? 'bg-white' : ''}`}>
          {/* Invoice Info and Amount Due (redundant with payment-link Invoice Details card) */}
          {variant !== 'paymentLink' && (
            <div className="border-b border-gray-200 px-6 py-6">
              <div className="text-center">
                {metadata?.invoiceId && (
                  <p className="text-sm text-gray-600">Invoice ID: INV-{metadata.invoiceId}</p>
                )}
                {customerName && (
                  <p className="text-sm text-gray-600">
                    Customer: {customerName}
                    {propertyAddress && ` • ${propertyAddress}`}
                  </p>
                )}
                <h2 className="mt-3 text-3xl font-bold text-gray-900">
                  Amount Due: ${amount.toFixed(2)}
                </h2>
              </div>
            </div>
          )}

          {/* Services Performed Header */}
          <div className="border-b border-gray-200 px-6 py-4">
            <h3 className="font-semibold text-gray-900">Services Performed</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Description
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                    Qty
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Unit Price
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {lineItems.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 text-sm text-gray-900">{item.description}</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-900">{item.quantity}</td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">
                      ${item.unitPrice.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                      ${item.total.toFixed(2)}
                      {item.discount !== undefined && item.discount > 0 && (
                        <div className="text-xs font-medium text-green-700">
                          Discount -${item.discount.toFixed(2)}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              {subtotal !== undefined && (
                <tfoot>
                  <tr className="border-t border-gray-200">
                    <td
                      colSpan={3}
                      className="px-6 py-3 text-right text-sm font-semibold text-gray-900"
                    >
                      Subtotal:
                    </td>
                    <td className="px-6 py-3 text-right text-sm font-semibold text-gray-900">
                      ${subtotal.toFixed(2)}
                    </td>
                  </tr>
                  {discountAmount !== undefined && discountAmount > 0 && (
                    <tr>
                      <td colSpan={3} className="px-6 py-2 text-right text-sm text-gray-900">
                        Discount:
                      </td>
                      <td className="px-6 py-2 text-right text-sm text-green-600">
                        -${discountAmount.toFixed(2)}
                      </td>
                    </tr>
                  )}
                  {taxAmount !== undefined && taxAmount > 0 && (
                    <tr>
                      <td colSpan={3} className="px-6 py-2 text-right text-sm text-gray-900">
                        Tax:
                      </td>
                      <td className="px-6 py-2 text-right text-sm text-gray-900">
                        ${taxAmount.toFixed(2)}
                      </td>
                    </tr>
                  )}
                  {retainageAmount !== undefined && retainageAmount !== 0 && (
                    <tr>
                      <td colSpan={3} className="px-6 py-2 text-right text-sm text-gray-900">
                        Retainage:
                      </td>
                      <td
                        className={`px-6 py-2 text-right text-sm ${
                          retainageAmount < 0 ? 'text-red-600' : 'text-green-600'
                        }`}
                      >
                        {retainageAmount >= 0 ? '+' : '-'}${Math.abs(retainageAmount).toFixed(2)}
                      </td>
                    </tr>
                  )}
                  <tr className="border-t border-gray-200">
                    <td
                      colSpan={3}
                      className="px-6 py-4 text-right text-base font-bold text-gray-900"
                    >
                      Total Amount Due:
                    </td>
                    <td className="px-6 py-4 text-right text-xl font-bold text-gray-900">
                      ${amount.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Card>
      )}

      {/* Error Display */}
      {error && (
        <Card className="border-red-200 bg-red-50 p-4">
          <div className="flex items-start">
            <AlertCircle className="mr-3 h-5 w-5 text-red-600" />
            <div>
              <h4 className="font-medium text-red-800">Payment Error</h4>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Payment Action */}
      <Card className={`p-6 ${variant === 'paymentLink' ? 'bg-white' : ''}`}>
        <div className="space-y-6">
          <div className={variant === 'paymentLink' ? undefined : 'text-center'}>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              {mode === 'tokenize' ? 'Add card on file' : 'Ready to Pay'}
            </h3>
            <p className="text-sm text-gray-600">
              {mode === 'tokenize'
                ? 'Continue to securely enter your card. No charge will be made.'
                : 'Click the button below to securely enter your payment information.'}
            </p>
          </div>

          {/* Form Actions */}
          <div
            className={`flex ${showCancelButton ? 'justify-between' : 'justify-center'} space-x-4`}
          >
            {showCancelButton && (
              <Button
                type="button"
                variant="outline"
                onClick={handleHelcimCancelButton}
                disabled={isLoading || paymentStatus === 'processing'}
                className="flex-1"
              >
                Cancel
              </Button>
            )}
            <Button
              type="button"
              onClick={handlePayClick}
              disabled={isLoading || paymentStatus === 'processing' || !checkoutToken}
              className={`flex-1 text-white disabled:bg-gray-400 ${
                variant === 'paymentLink'
                  ? 'bg-gray-900 hover:bg-gray-800'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : paymentStatus === 'processing' ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : mode === 'tokenize' ? (
                <>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Add card
                </>
              ) : (
                <>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Pay ${amount.toFixed(2)}
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>

      {/* Security Notice */}
      <div className="text-center text-xs text-gray-500">
        <p>Your payment information is encrypted and secure</p>
        <p>Powered by Helcim</p>
      </div>
    </div>
  );
};
